import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppointment, useUpdateAppointment } from '@/hooks';
import { useStores } from '@/hooks';
import { appointmentsApi } from '@/lib/api';
import { toast } from 'sonner';
import type { LookupAppointmentFormData } from '@geely/shared';

type PendingAction = 'cancel' | 'reschedule' | null;

function isGuestTokenValid(): boolean {
  const token = localStorage.getItem('geely_guest_token');
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function useLookupAppointment() {
  const [searchedId, setSearchedId] = useState<string | null>(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();

  const { data: stores = [] } = useStores();
  const { data: foundAppointment, isLoading } = useAppointment(searchedId || undefined);
  const updateAppointmentMutation = useUpdateAppointment();

  const store = foundAppointment ? stores.find((s) => s.id === foundAppointment.storeId) : null;

  const isPending = foundAppointment?.status === 'pending';

  const onSearch = (data: LookupAppointmentFormData) => {
    setSearchedId(data.appointmentId.trim());
  };

  const validateIsPending = (action: string): boolean => {
    if (!foundAppointment) return false;

    if (!isPending) {
      toast.error(`Apenas agendamentos pendentes podem ser ${action}.`);
      return false;
    }

    return true;
  };

  const executeAction = async (action: PendingAction) => {
    if (action === 'cancel') {
      await cancelAppointment();
    } else if (action === 'reschedule') {
      await rescheduleAppointment();
    }
  };

  const ensureGuestToken = async (action: PendingAction): Promise<boolean> => {
    if (isGuestTokenValid()) return true;

    setPendingAction(action);
    setShowEmailDialog(true);
    return false;
  };

  const handleEmailVerification = async (email: string) => {
    if (!foundAppointment) return;

    setIsVerifying(true);
    try {
      const { data } = await appointmentsApi.generateGuestToken(
        foundAppointment.id,
        email
      );
      localStorage.setItem('geely_guest_token', data.token);
      setShowEmailDialog(false);

      await executeAction(pendingAction);
      setPendingAction(null);
    } catch {
      toast.error('Email não corresponde ao agendamento.');
    } finally {
      setIsVerifying(false);
    }
  };

  const closeEmailDialog = () => {
    setShowEmailDialog(false);
    setPendingAction(null);
  };

  const cancelAppointment = async () => {
    if (!foundAppointment) return;

    try {
      await updateAppointmentMutation.mutateAsync({
        id: foundAppointment.id,
        data: { status: 'cancelled' },
      });
      toast.success('Agendamento cancelado com sucesso!');
      setSearchedId(foundAppointment.id);
    } catch {
      toast.error('Erro ao cancelar agendamento');
    }
  };

  const rescheduleAppointment = async () => {
    if (!foundAppointment) return;

    try {
      const { data: fullAppointment } = await appointmentsApi.getFullById(foundAppointment.id);
      navigate('/booking', {
        state: { rescheduleAppointment: fullAppointment },
      });
    } catch {
      toast.error('Erro ao carregar dados do agendamento para remarcação.');
    }
  };

  const handleCancel = async () => {
    if (!validateIsPending('cancelados')) return;

    const hasToken = await ensureGuestToken('cancel');
    if (!hasToken) return;

    const confirmed = confirm('Tem certeza que deseja cancelar este agendamento?');
    if (confirmed) await cancelAppointment();
  };

  const handleReschedule = async () => {
    if (!validateIsPending('remarcados')) return;

    const hasToken = await ensureGuestToken('reschedule');
    if (!hasToken) return;

    await rescheduleAppointment();
  };

  return {
    searchedId,
    foundAppointment,
    isLoading,
    store,
    onSearch,
    handleCancel,
    handleReschedule,
    // Email verification
    showEmailDialog,
    isVerifying,
    handleEmailVerification,
    closeEmailDialog,
  };
}
